import { useContext, useMemo, useState } from "react";
import {
  APPLICATION_TAB_NAMES,
  JOB_APPLICATION_MATCH_STATUS,
  JOB_APPLICATION_MATCH_SUB_STATUS,
  JOB_OPPORTUNITY_STATUSES,
} from "lookup";
import { JobsContext, MatchContext, ModalContext } from "context/providers";
import SvgIcon from "components/base/SvgIcon";
import { getButtonText } from "components/UserCardList/helpers/userCard";
import { isMatchPreShortlistedStatusType } from "utils/helpers/match";
import { cn } from "utils";
import ContactPopover from "./ContactPopover";
import ListViewResumeButton from "./ListViewResumeButton";
import MatchActionDropDown, { MENU_ACTIONS } from "./MatchActionDropDown";
import HitActionDropDown from "./HitActionDropDown";
import {
  CtaVerticalDivider,
  ListViewIconButton,
} from "../shared/CtaPrimitives";
import { Star, ThumbsDown } from "lucide-react";
import AppDialog from "components/AppDialog";
import RejectionForm from "../UserCardResult/molecules/RejectionForm";
import { CTA_TITLES } from "components/UserCardList/constants/ctaTitles";

const ListViewRowActions = ({
  hit,
  row,
  activeTabName,
  collectionKey,
  tabUsesMatches,
  showFullProfileMenuItem = false,
  hideMatchActions = false,
}) => {
  const { showModal } = useContext(ModalContext);
  const { jobOpp, convertJobLongListToMatch } = useContext(JobsContext);
  const { updateMatch: contextUpdateMatch, createMatch } =
    useContext(MatchContext);

  const [isLoading, setIsLoading] = useState({
    [JOB_APPLICATION_MATCH_STATUS.MATCHED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SHORTLISTED]: false,
    [JOB_APPLICATION_MATCH_STATUS.SKIPPED]: false,
    [JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST]: false,
    [JOB_APPLICATION_MATCH_SUB_STATUS.NULL]: false,
    [JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]: false,
  });

  const [showRejectModal, setShowRejectModal] = useState(false);

  const oneOfButtonsLoading = useMemo(
    () => Object.values(isLoading).some(Boolean),
    [isLoading]
  );

  const isJobStatusActive = jobOpp.status === JOB_OPPORTUNITY_STATUSES.ACTIVE;
  const hasPrimaryMatcher = !!jobOpp.torcOwner?.username;

  const isLongList = collectionKey === "longList";
  const canConvertToMatch = Boolean(hit.match?.canConvertToMatch);
  const longListActionsEnabled = isLongList && canConvertToMatch;

  // Long-list rows have canConvertToMatch but no match.status; do not use match-tab flow.
  const usesMatchTabFlow = tabUsesMatches && !isLongList;

  const matchStatus = usesMatchTabFlow
    ? hit.match?.status
    : hit.associatedMatch?.status;

  const canBeMatched =
    longListActionsEnabled ||
    (usesMatchTabFlow &&
      (isMatchPreShortlistedStatusType(matchStatus) ||
        matchStatus === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED ||
        matchStatus === JOB_APPLICATION_MATCH_STATUS.INTERESTED));

  const canBeSkipped =
    longListActionsEnabled ||
    (usesMatchTabFlow &&
      (activeTabName === APPLICATION_TAB_NAMES.SHORTLISTED ||
        activeTabName === APPLICATION_TAB_NAMES.PRECANDIDATES) &&
      (isMatchPreShortlistedStatusType(matchStatus) ||
        matchStatus === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED ||
        matchStatus === JOB_APPLICATION_MATCH_STATUS.INTERESTED ||
        matchStatus === JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK));

  const hitCanBeMatched =
    !usesMatchTabFlow &&
    !longListActionsEnabled &&
    matchStatus &&
    (isMatchPreShortlistedStatusType(matchStatus) ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.INTERESTED);

  const hitCanBeSkipped =
    !usesMatchTabFlow &&
    !longListActionsEnabled &&
    matchStatus &&
    (isMatchPreShortlistedStatusType(matchStatus) ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED);

  const heartEnabled = longListActionsEnabled
    ? true
    : usesMatchTabFlow
    ? canBeMatched
    : !hit.associatedMatch || hitCanBeMatched;

  const bookmarkEnabled =
    longListActionsEnabled || (!usesMatchTabFlow && !hit.associatedMatch);

  const skipEnabled = longListActionsEnabled
    ? true
    : usesMatchTabFlow
    ? canBeSkipped
    : !hit.associatedMatch || hitCanBeSkipped;

  const showFinalistAction =
    usesMatchTabFlow &&
    (matchStatus === JOB_APPLICATION_MATCH_STATUS.APPLIED ||
      matchStatus === JOB_APPLICATION_MATCH_STATUS.MATCHED);

  const isNotFinalist =
    hit.match?.subStatus !== JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST;

  const showRejectMatched =
    usesMatchTabFlow && activeTabName === APPLICATION_TAB_NAMES.MATCHED;

  const { applicationId, jobOpportunityId } = useMemo(() => {
    const app = Array.isArray(hit.applications)
      ? hit.applications.find((a) => a.jobTypeId === jobOpp.jobType?.id)
      : undefined;

    return {
      applicationId:
        hit?.match?.applicationId ||
        hit?.associatedMatch?.applicationId ||
        app?.id ||
        `User#${hit.id}`,
      jobOpportunityId: jobOpp?.id,
    };
  }, [hit, jobOpp]);

  const matchAction = async (args, isUpdate) => {
    if (args.status === JOB_APPLICATION_MATCH_STATUS.MATCHED) {
      showModal({
        type: "match",
        applicationId,
        jobOpportunityId,
        userId: hit.id,
        userIsUnclaimed: hit.isUnclaimed,
        collectionKey,
        status: args.status,
        isUpdate,
        isJobLongList: hit.match?.canConvertToMatch,
        resumeLocation: hit.match?.resumeLocation,
        hitStatus: hit.match?.status || hit.associatedMatch?.status,
      });
      return;
    }

    setIsLoading((prev) => ({ ...prev, [args.status]: true }));

    if (hit.isUnclaimed) {
      args.unclaimedWhenMatched = true;
    }

    const matchAttrs = {
      ...args,
      ...(hit.match?.canConvertToMatch && hit.match?.resumeLocation
        ? { resumeLocation: hit.match.resumeLocation }
        : {}),
    };

    try {
      if (isUpdate) {
        await contextUpdateMatch(applicationId, jobOpportunityId, matchAttrs);
      } else {
        await createMatch(jobOpportunityId, hit.id, matchAttrs);
        if (hit.match?.canConvertToMatch) {
          await convertJobLongListToMatch(jobOpportunityId, hit.id);
        }
      }
    } catch (error) {
      console.log("matchAction error: ", error);
    } finally {
      setIsLoading((prev) => ({ ...prev, [args.status]: false }));
    }
  };

  const handleMatchClick = () => {
    if (longListActionsEnabled) {
      matchAction({ status: JOB_APPLICATION_MATCH_STATUS.MATCHED }, false);
      return;
    }

    const hasPlacements = usesMatchTabFlow
      ? hit.match?.user?.hasActivePlacements
      : hit.hasActivePlacements;

    if (usesMatchTabFlow) {
      if (!hasPlacements) {
        showModal({
          type: "match",
          applicationId,
          jobOpportunityId,
          userId: hit.id,
          userIsUnclaimed: hit.isUnclaimed,
          collectionKey,
          status: JOB_APPLICATION_MATCH_STATUS.MATCHED,
          isUpdate: true,
          hitStatus: hit.match?.status,
        });
      } else if (
        window.confirm(
          "Are you sure you want to match this user, even though they have active engagement(s) currently?"
        )
      ) {
        showModal({
          type: "match",
          applicationId,
          jobOpportunityId,
          userId: hit.id,
          userIsUnclaimed: hit.isUnclaimed,
          collectionKey,
          status: JOB_APPLICATION_MATCH_STATUS.MATCHED,
          isUpdate: true,
          hitStatus: hit.match?.status,
        });
      }
      return;
    }

    if (!hasPlacements) {
      matchAction(
        { status: JOB_APPLICATION_MATCH_STATUS.MATCHED },
        !!matchStatus
      );
    } else if (
      window.confirm(
        "Are you sure you want to match this user, even though they have active engagement(s) currently?"
      )
    ) {
      matchAction(
        { status: JOB_APPLICATION_MATCH_STATUS.MATCHED },
        !!matchStatus
      );
    }
  };

  const handleFinalistClick = async () => {
    const newSubStatus = isNotFinalist
      ? JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST
      : JOB_APPLICATION_MATCH_SUB_STATUS.NULL;

    if (
      newSubStatus === JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST &&
      jobOpp.isDMP
    ) {
      showModal({
        type: "finalist",
        applicationId,
        jobOpportunityId,
        matchParams: { subStatus: newSubStatus },
        match: hit.match,
        collectionKey,
        userId: hit.match?.userId || hit.id,
        isUpdate: true,
      });
      return;
    }

    setIsLoading((prev) => ({ ...prev, [newSubStatus]: true }));

    try {
      await contextUpdateMatch(applicationId, jobOpportunityId, {
        subStatus: newSubStatus,
      });
    } catch (error) {
      console.log("finalist error: ", error);
    } finally {
      setIsLoading((prev) => ({ ...prev, [newSubStatus]: false }));
    }
  };

  const handleRejectClick = () => {
    setShowRejectModal(true);
  };

  const heartDisabled =
    oneOfButtonsLoading || !isJobStatusActive || !hasPrimaryMatcher;

  const hasPlacementsWarning = usesMatchTabFlow
    ? hit.match?.user?.hasActivePlacements
    : hit.hasActivePlacements;

  const showMatchActions =
    !hideMatchActions && activeTabName !== APPLICATION_TAB_NAMES.MATCHED;

  // Whatever this row renders as an icon button is dropped from the kebab
  // menu, so no action shows up twice on the card.
  const hiddenMenuActions = [
    ...(showMatchActions ? [MENU_ACTIONS.MATCH, MENU_ACTIONS.SKIP] : []),
    ...(showFinalistAction ? [MENU_ACTIONS.FINALIST] : []),
    ...(showRejectMatched ? [MENU_ACTIONS.REJECT] : []),
  ];

  return (
    <div
      className="flex items-center gap-0.5 shrink-0"
      onClick={(e) => e.stopPropagation()}
    >
      {showMatchActions && (
        <>
          <ListViewIconButton
            testId="hit-match-button"
            title={
              !isJobStatusActive
                ? "This job is not active"
                : !hasPrimaryMatcher
                ? "This job has no primary matcher"
                : hasPlacementsWarning
                ? "User has active engagement(s)"
                : CTA_TITLES.match
            }
            disabled={heartDisabled || !heartEnabled}
            onClick={handleMatchClick}
          >
            <SvgIcon
              type={hasPlacementsWarning ? "warning" : "heart"}
              className="w-5 h-5 fill-current"
            />
          </ListViewIconButton>

          <ListViewIconButton
            testId="hit-shortlist-button"
            title={getButtonText("shortlist")}
            disabled={oneOfButtonsLoading || !bookmarkEnabled}
            onClick={() =>
              matchAction(
                { status: JOB_APPLICATION_MATCH_STATUS.SHORTLISTED },
                !!matchStatus
              )
            }
          >
            <SvgIcon type="focus" className="w-5 h-5 fill-current" />
          </ListViewIconButton>

          <ListViewIconButton
            title={getButtonText("skip")}
            disabled={oneOfButtonsLoading || !skipEnabled}
            onClick={async () => {
              if (usesMatchTabFlow) {
                setIsLoading((prev) => ({
                  ...prev,
                  [JOB_APPLICATION_MATCH_STATUS.SKIPPED]: true,
                }));
                try {
                  await contextUpdateMatch(applicationId, jobOpportunityId, {
                    status: JOB_APPLICATION_MATCH_STATUS.SKIPPED,
                  });
                } catch (error) {
                  console.log("skip error: ", error);
                } finally {
                  setIsLoading((prev) => ({
                    ...prev,
                    [JOB_APPLICATION_MATCH_STATUS.SKIPPED]: false,
                  }));
                }
              } else {
                matchAction(
                  { status: JOB_APPLICATION_MATCH_STATUS.SKIPPED },
                  !!matchStatus
                );
              }
            }}
          >
            <SvgIcon type="skip" className="w-5 h-5 fill-current" />
          </ListViewIconButton>
        </>
      )}

      {showFinalistAction && (
        <ListViewIconButton
          title={isNotFinalist ? "Mark as Finalist" : "Remove Finalist"}
          disabled={oneOfButtonsLoading}
          onClick={handleFinalistClick}
          className={cn(
            !isNotFinalist &&
              !isLoading[JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST] &&
              "hover:!text-red-500"
          )}
        >
          <Star className={cn("w-5 h-5", !isNotFinalist && "fill-current")} />
        </ListViewIconButton>
      )}

      {showRejectMatched && (
        <ListViewIconButton
          title="Reject"
          disabled={oneOfButtonsLoading}
          onClick={handleRejectClick}
        >
          <ThumbsDown className="w-5 h-5" />
        </ListViewIconButton>
      )}

      <ContactPopover hit={hit} />

      <ListViewResumeButton hit={hit} />

      <CtaVerticalDivider />

      {usesMatchTabFlow ? (
        <MatchActionDropDown
          row={row}
          activeTabName={activeTabName}
          collectionKey={collectionKey}
          hiddenActions={hiddenMenuActions}
          showFullProfileMenuItem={showFullProfileMenuItem}
        />
      ) : (
        <HitActionDropDown
          row={row}
          activeTabName={activeTabName}
          collectionKey={collectionKey}
          hidePrimaryActionsInMenu
          hideMatchActions={
            hideMatchActions ||
            activeTabName === APPLICATION_TAB_NAMES.SEARCHRESULTS
          }
          showFullProfileMenuItem={showFullProfileMenuItem}
        />
      )}

      <AppDialog
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        header="Rejection Details"
        className="max-h-[90vh] w-[95%] max-w-none overflow-x-hidden rounded-lg md:w-10/12 lg:w-6/12"
        contentClassName="px-8 pb-8"
      >
        <RejectionForm
          applicationId={applicationId}
          jobOpportunityId={jobOpportunityId}
          isVisible={showRejectModal}
          onClose={() => setShowRejectModal(false)}
        />
      </AppDialog>
    </div>
  );
};

export default ListViewRowActions;
